import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import GeoLocationInput from "@/components/GeoLocationInput";
import FileUploadZone from "@/components/FileUploadZone";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { enrichLabelWithYear } from "@/lib/exerciseYear";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { HardHat, ChevronRight } from "lucide-react";
import { FI, YN } from "@/components/FormFields";
import { StepProgress, FormNavigation } from "@/components/StepProgress";
import ClaimsInfoSheet, { emptyClaim } from "@/components/ClaimsInfoSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ========== CIS CONFIG - PROFESSIONI TECNICHE (dal PDF Furness/Lloyd's) ========== */
const CIS_TECNICA_CONFIG = {
  professioni: [
    "Architetto", "Ingegnere", "Ingegnere Navale", "Geologo",
    "Geometra / Perito Edile", "Perito Industriale", "Restauratore", "Chimico",
    "Consulente sulla Sicurezza", "Agronomo / Agrotecnici",
    "Studio di Architetti / Ingegneri", "Studio Associato professioni miste",
    "Altro (specificare)",
  ],
  categorieDanno: [
    { id: "errore_progettazione", label: "Errore nel disegno / stima / progettazione" },
    { id: "danni_fisici_terzi", label: "Danni fisici subiti da terzi" },
    { id: "incidente_mortale", label: "Incidente mortale sul luogo di lavoro" },
    { id: "errore_materiali", label: "Errore nei materiali usati" },
    { id: "mancata_autorizzazione", label: "Mancata autorizzazione / Errore amministrativo" },
    { id: "danni_materiali_terzi", label: "Danni materiali causati a terzi" },
    { id: "inquinamento", label: "Inquinamento accidentale" },
    { id: "errore_valutazione", label: "Errore nella valutazione / collaudo" },
    { id: "mancato_rispetto_norme", label: "Mancato rispetto norme e regolamenti" },
    { id: "mancato_rispetto_sicurezza", label: "Mancato rispetto normative sulla sicurezza" },
    { id: "altro", label: "Altro (specificare)" },
  ],
  incarichi: [
    { id: "progettista", label: "Progettista" },
    { id: "direttore_lavori", label: "Direttore dei lavori / Direttore operativo" },
    { id: "direttore_cantiere", label: "Direttore cantiere" },
    { id: "assistente_tecnico", label: "Assistente tecnico" },
    { id: "coordinatore_progettazione", label: "Coordinatore della progettazione" },
    { id: "calcolatore_statico", label: "Calcolatore statico" },
    { id: "collaudatore", label: "Collaudatore" },
    { id: "responsabile_sicurezza", label: "Responsabile della sicurezza" },
    { id: "calcolatore_cemento", label: "Calcolatore cemento armato" },
    { id: "validatore", label: "Validatore" },
    { id: "altro", label: "Altro (specificare)" },
  ],
};

const CATEGORIES = [
  { id: "architetto", label: "Architetto", template: "scudo" },
  { id: "ingegnere", label: "Ingegnere", template: "scudo" },
  { id: "ingegnere_navale", label: "Ingegnere Navale", template: "scudo" },
  { id: "geologo", label: "Geologo", template: "scudo" },
  { id: "geometra_perito_edile", label: "Geometra / Perito Edile", template: "scudo" },
  { id: "perito_industriale", label: "Perito Industriale", template: "scudo" },
  { id: "restauratore", label: "Restauratore", template: "scudo" },
  { id: "consulente_sicurezza", label: "Consulente sulla Sicurezza", template: "scudo" },
  { id: "studio_arch_ing", label: "Studio/Società di Architetti/Ingegneri", template: "scudo" },
  { id: "studio_ing_navale", label: "Studio/Società di Ingegneria Navale", template: "scudo" },
  { id: "studio_geologi", label: "Studio/Società di Geologi", template: "scudo" },
  { id: "studio_sicurezza", label: "Studio/Società Consulenti sulla Sicurezza", template: "scudo" },
  { id: "studio_miste", label: "Studio/Società professioni miste", template: "scudo" },
  { id: "ingegnere_chimico", label: "Ingegnere Chimico", template: "chimici" },
  { id: "perito_chimico", label: "Perito Chimico", template: "chimici" },
  { id: "chimico", label: "Chimico", template: "chimici" },
  { id: "agrotecnico", label: "Agrotecnico / Agrotecnico Laureato", template: "agro" },
  { id: "dottore_agronomo", label: "Dottore Agronomo / Forestale", template: "agro" },
  { id: "perito_agrario", label: "Perito Agrario", template: "agro" },
  { id: "esperto_forestale", label: "Esperto Forestale / Silvicoltore", template: "agro" },
  { id: "tecnologo_alimentare", label: "Tecnologo Alimentare", template: "agro" },
  { id: "asseverazione", label: "Attività di Asseverazione", template: "progetto" },
  { id: "certificatore_5_0", label: "Certificatore Transizione 5.0", template: "progetto" },
  { id: "merloni", label: "Progettazione/Validazione Merloni", template: "progetto" },
];

const STEPS = {
  scudo: ["Anagrafica", "Informazioni Finanziarie", "Estensioni di Copertura", "Prodotti Addizionali", "Sinistrosità", "Assicurazioni Precedenti", "Dichiarazioni"],
  chimici: ["Anagrafica", "Informazioni Finanziarie", "Estensioni di Copertura", "Prodotti Addizionali", "Sinistrosità", "Assicurazioni Precedenti", "Dichiarazioni"],
  agro: ["Anagrafica", "Informazioni Finanziarie", "Informazioni Generali", "Condizioni di Polizza", "Sinistrosità", "Assicurazioni Precedenti", "Dichiarazioni"],
  progetto: ["Anagrafica", "Informazioni sul Rischio", "Sinistrosità", "Assicurazioni Precedenti", "Dichiarazioni"],
};

export default function AreaTecnicaPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step, selectedCategory]);

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const handleFileUpload = (k, d) => { setFiles(prev => { const n = { ...prev }; if (d) n[k] = d; else delete n[k]; return n; }); };

  const showClaimsSheet = formData.sinistri_5 === "si" || formData.fatti_risc === "si";
  useEffect(() => {
    if (showClaimsSheet && (!formData.sinistri_claims || formData.sinistri_claims.length === 0)) {
      setFormData(prev => ({ ...prev, sinistri_claims: [emptyClaim()] }));
    }
  }, [showClaimsSheet, formData.sinistri_claims]);

  const cat = CATEGORIES.find(c => c.id === selectedCategory);
  const template = cat?.template || "scudo";
  const steps = STEPS[template];
  const totalSteps = steps?.length || 7;
  const progress = ((step + 1) / totalSteps) * 100;
  const isStudio = selectedCategory?.startsWith("studio");

  const handleSubmit = async () => {
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono) {
      toast.error("Compila tutti i campi obbligatori dell'anagrafica"); return;
    }
    if (!formData.privacy) { toast.error("Devi accettare la privacy policy"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        type: "area_tecnica", data: { ...formData, categoria: selectedCategory, template },
        files: Object.values(files),
        contact: { nome: formData.nome, cognome: formData.cognome, email: formData.email, telefono: formData.telefono }
      });
      navigate("/conferma");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio."); }
    finally { setSubmitting(false); }
  };

  if (!selectedCategory) {
    return (
      <><Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="area-tecnica-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <HardHat className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#0F1F14] mb-3" data-testid="tecnica-title">
              RC Professioni Tecniche
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-lg mx-auto">
              Architetti, Ingegneri, Geologi, Geometri, Chimici, Agronomi e molto altro. Seleziona la tua professione.
            </p>
          </div>
          <div className="space-y-2">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                className="w-full text-left bg-white rounded-2xl border border-[#E5E2DB] p-4 sm:p-5 premium-card hover:border-[#163321]/30 transition-all group"
                data-testid={`tec-cat-${c.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-sm sm:text-base font-medium text-[#0F1F14] group-hover:text-[#163321] transition-colors">{c.label}</h3>
                  <ChevronRight className="w-5 h-5 text-[#4A5E51] group-hover:text-[#163321] group-hover:translate-x-1 transition-all shrink-0 ml-4" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </main><Footer /></>
    );
  }

  const renderStep = () => {
    const s = steps[step];
    if (s === "Anagrafica") return renderAnagrafica();
    if (s === "Informazioni Finanziarie") return renderFinanziarie();
    if (s === "Estensioni di Copertura") return renderEstensioni();
    if (s === "Prodotti Addizionali") return renderProdotti();
    if (s === "Informazioni Generali") return renderInfoGenerali();
    if (s === "Condizioni di Polizza") return renderCondizioni();
    if (s === "Informazioni sul Rischio") return renderProgetto();
    if (s === "Sinistrosità") return renderSinistri();
    if (s === "Assicurazioni Precedenti") return renderAssicPrec();
    if (s === "Dichiarazioni") return renderDichiarazioni();
    return null;
  };

  const renderAnagrafica = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Nome" required value={formData.nome} onChange={v => update("nome", v)} tid="tec-nome" />
        <FI label="Cognome" required value={formData.cognome} onChange={v => update("cognome", v)} tid="tec-cognome" />
      </div>
      <GeoLocationInput label="Sede Legale o Residenza" required value={formData.indirizzo} onChange={v => update("indirizzo", v)} testId="tec-indirizzo" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FI label="Citt&agrave;" required value={formData.citta} onChange={v => update("citta", v)} tid="tec-citta" />
        <FI label="Provincia" value={formData.provincia} onChange={v => update("provincia", v)} tid="tec-prov" />
        <FI label="CAP" value={formData.cap} onChange={v => update("cap", v)} tid="tec-cap" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Codice Fiscale" required value={formData.cf} onChange={v => update("cf", v.toUpperCase())} tid="tec-cf" />
        <FI label="Partita IVA" required value={formData.piva} onChange={v => update("piva", v)} tid="tec-piva" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Email" required type="email" value={formData.email} onChange={v => update("email", v)} tid="tec-email" placeholder="la.tua@email.it" />
        <FI label="Telefono cellulare" required type="tel" value={formData.telefono} onChange={v => update("telefono", v)} tid="tec-tel" placeholder="+39 333 1234567" />
      </div>
      <FI label="Anno iscrizione all'albo" value={formData.anno_albo} onChange={v => update("anno_albo", v)} tid="tec-anno-albo" />
      {isStudio && (
        <div className="space-y-4 p-4 bg-[#F5F4F0] rounded-xl">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4A5E51]">Dati Studio / Societ&agrave;</p>
          <FI label="Ragione / Denominazione Sociale" required value={formData.ragione_sociale} onChange={v => update("ragione_sociale", v)} tid="tec-rag-soc" />
          <FI label="Anno di costituzione" value={formData.anno_cost} onChange={v => update("anno_cost", v)} tid="tec-anno-cost" />
          <div className="space-y-2">
            <Label className="text-sm text-[#0F1F14]">Professionisti iscritti all'albo / collaboratori in garanzia</Label>
            <Textarea value={formData.prof_studio || ""} onChange={e => update("prof_studio", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="Nome e Cognome - Anno iscrizione (uno per riga)" data-testid="tec-prof-studio" />
          </div>
        </div>
      )}
    </div>
  );

  const renderFinanziarie = () => (
    <div className="space-y-5">
      <FI label={enrichLabelWithYear("Fatturato ultimo esercizio finanziario completo (\u20ac)")} required value={formData.fatt_ultimo} onChange={v => update("fatt_ultimo", v)} type="number" tid="tec-fatt-ult" />
      <FI label={enrichLabelWithYear("Fatturato penultimo esercizio (\u20ac)")} required value={formData.fatt_pen} onChange={v => update("fatt_pen", v)} type="number" tid="tec-fatt-pen" />
      <FI label="Stima fatturato esercizio corrente (&euro;)" required value={formData.fatt_stima} onChange={v => update("fatt_stima", v)} type="number" tid="tec-fatt-stima" />
      <YN label="Professionista neo abilitato o Studio/Societ&agrave; neo costituita?" field="neo_abilitato" formData={formData} update={update} tid="tec-neo" />
      {formData.neo_abilitato === "si" && (
        <FI label="Fatturato previsto primi 12 mesi (&euro;)" required value={formData.fatt_prev} onChange={v => update("fatt_prev", v)} type="number" tid="tec-fatt-prev" />
      )}
    </div>
  );

  const renderEstensioni = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Seleziona le estensioni di copertura desiderate.</p>
      <YN label="Scoperto 10% (min &euro;3.000 / max &euro;10.000) per sinistri superiori a &euro;20.000?" field="scoperto_10" formData={formData} update={update} tid="tec-scoperto" />
      <YN label="Attivit&agrave; D.Lgs 81/2008 (Sicurezza sul lavoro)?" field="dlgs_81" formData={formData} update={update} tid="tec-dlgs81" />
      <YN label="Attivit&agrave; D.Lgs 624/1996?" field="dlgs_624" formData={formData} update={update} tid="tec-dlgs624" />
      <YN label="Copertura valida solo per attivit&agrave; D.Lgs 81/2008 e 624/1996?" field="solo_sicurezza" formData={formData} update={update} tid="tec-solo-sic" />
      {(template === "scudo") && (
        <>
          <YN label="Attivit&agrave; ferrovie, funivie, gallerie, porti, dighe, opere subacquee, parchi divertimento?" field="opere_alto_rischio" formData={formData} update={update} tid="tec-alto-rischio" />
          <YN label="Clausola GENERAL CONTRACTOR?" field="general_contractor" formData={formData} update={update} tid="tec-gen-contr" />
        </>
      )}
      {(template === "chimici") && (
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Attivit&agrave; Alta Specializzazione (Opere ad Alto Rischio)</Label>
          <Select value={formData.alta_spec || ""} onValueChange={v => update("alta_spec", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="tec-alta-spec"><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">Non richiesta</SelectItem>
              <SelectItem value="fino_25">Fino al 25% del fatturato</SelectItem>
              <SelectItem value="25_50">Tra il 25% e il 50% del fatturato</SelectItem>
              <SelectItem value="oltre_50">Oltre il 50% del fatturato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <YN label="Sistema di gestione qualit&agrave;?" field="qualita" formData={formData} update={update} tid="tec-qualita" />
      <YN label="Perdite patrimoniali con sottolimite 20% del massimale?" field="perdite_patrim" formData={formData} update={update} tid="tec-perdite" />
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Garanzia postuma</Label>
        <Select value={formData.postuma || ""} onValueChange={v => update("postuma", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="tec-postuma"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="2_anni">S&igrave; - 2 Anni</SelectItem>
            <SelectItem value="5_anni">S&igrave; - 5 Anni</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <YN label="Data Protection Officer?" field="dpo" formData={formData} update={update} tid="tec-dpo" />
      <YN label="Sicurezza informatica e perdita dei dati?" field="sicurezza_info" formData={formData} update={update} tid="tec-sic-info" />
      <YN label="Copertura responsabilit&agrave; amministrativa?" field="resp_amm" formData={formData} update={update} tid="tec-resp-amm" />
      <YN label="Responsabilit&agrave; in solido?" field="resp_solido" formData={formData} update={update} tid="tec-resp-sol" />
      <YN label="Direzione lavori?" field="dir_lavori" formData={formData} update={update} tid="tec-dir-lav" />
      {formData.dir_lavori === "si" && (
        <YN label="Scoperto 10% (min &euro;5.000 / max &euro;10.000) per Direzione Lavori?" field="scoperto_dl" formData={formData} update={update} tid="tec-scop-dl" />
      )}
    </div>
  );

  const renderProdotti = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Prodotti addizionali disponibili.</p>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Estensione &quot;Tutela Legale&quot;</Label>
        <Select value={formData.tutela_legale || ""} onValueChange={v => update("tutela_legale", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="tec-tutela"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="15000">S&igrave; - massimale &euro;15.000</SelectItem>
            <SelectItem value="30000">S&igrave; - massimale &euro;30.000</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderInfoGenerali = () => (
    <div className="space-y-5">
      <YN label="Cambiamenti sostanziali nella natura delle attivit&agrave; professionali nei prossimi 12 mesi?" field="cambiamenti" formData={formData} update={update} tid="tec-cambiamenti" />
      {formData.cambiamenti === "si" && (
        <Textarea value={formData.det_cambiamenti || ""} onChange={e => update("det_cambiamenti", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Descrivi i cambiamenti..." data-testid="tec-det-camb" />
      )}
      {(selectedCategory?.includes("agro") || selectedCategory === "perito_agrario" || selectedCategory === "esperto_forestale") && (
        <YN label="Attivit&agrave; professionale nel settore della floricultura?" field="floricultura" formData={formData} update={update} tid="tec-floricult" />
      )}
    </div>
  );

  const renderCondizioni = () => (
    <div className="space-y-5">
      <FI label="Massimale richiesto (per sinistro e in aggregato annuo)" value={formData.massimale} onChange={v => update("massimale", v)} tid="tec-massimale" />
      <FI label="Franchigia richiesta" value={formData.franchigia} onChange={v => update("franchigia", v)} tid="tec-franchigia" />
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Retroattivit&agrave; richiesta</Label>
        <Select value={formData.retro || ""} onValueChange={v => update("retro", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="tec-retro"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="decorrenza">Dalla decorrenza</SelectItem>
            <SelectItem value="1">1 Anno</SelectItem><SelectItem value="2">2 Anni</SelectItem>
            <SelectItem value="3">3 Anni</SelectItem><SelectItem value="5">5 Anni</SelectItem>
            <SelectItem value="illimitata">Illimitata</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <YN label="Scoperto 10% (min &euro;3.000 / max &euro;10.000) per sinistri superiori a &euro;20.000?" field="scoperto_10" formData={formData} update={update} tid="tec-scoperto" />
    </div>
  );

  const renderProgetto = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Dettagli del progetto oggetto della copertura.</p>
      <FI label="Descrizione del progetto (ubicazione e tipologia)" required value={formData.desc_progetto} onChange={v => update("desc_progetto", v)} tid="tec-desc-prog" />
      {selectedCategory === "merloni" && (
        <>
          <FI label="Stazione Appaltante" value={formData.stazione_appaltante} onChange={v => update("stazione_appaltante", v)} tid="tec-stazione" />
          <FI label="Luogo di esecuzione dell'opera" value={formData.luogo_opera} onChange={v => update("luogo_opera", v)} tid="tec-luogo" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FI label="Data prevista inizio lavori" type="date" value={formData.data_inizio} onChange={v => update("data_inizio", v)} tid="tec-data-inizio" />
            <FI label="Data prevista fine lavori" type="date" value={formData.data_fine} onChange={v => update("data_fine", v)} tid="tec-data-fine" />
          </div>
          <YN label="Svolgete attivit&agrave; di Progettazione Preliminare?" field="prog_prelim" formData={formData} update={update} tid="tec-prog-prel" />
          <YN label="Svolgete attivit&agrave; di Progettazione Definitiva?" field="prog_def" formData={formData} update={update} tid="tec-prog-def" />
          <YN label="Svolgete attivit&agrave; di Progettazione Esecutiva?" field="prog_esec" formData={formData} update={update} tid="tec-prog-esec" />
          <YN label="Disponete di un sistema di gestione qualit&agrave;?" field="qualita" formData={formData} update={update} tid="tec-qualita" />
        </>
      )}
      {selectedCategory === "certificatore_5_0" && (
        <>
          <FI label="Ragione Sociale del Beneficiario" value={formData.beneficiario} onChange={v => update("beneficiario", v)} tid="tec-benef" />
          <FI label="Codice Fiscale del Beneficiario" value={formData.cf_benef} onChange={v => update("cf_benef", v)} tid="tec-cf-benef" />
          <FI label="Importo del credito d'imposta certificato (&euro;)" type="number" value={formData.credito_imposta} onChange={v => update("credito_imposta", v)} tid="tec-credito" />
        </>
      )}
      <FI label="Valore totale del progetto (&euro;)" type="number" value={formData.valore_progetto} onChange={v => update("valore_progetto", v)} tid="tec-valore" />
      <FI label="Onorari ricevuti (&euro;)" type="number" value={formData.onorari} onChange={v => update("onorari", v)} tid="tec-onorari" />
    </div>
  );

  const renderSinistri = () => (
    <div className="space-y-5">
      <YN label="Richieste di risarcimento o azioni per accertare responsabilit&agrave; civile, penale o deontologica negli ultimi 5 anni?" field="sinistri_5" formData={formData} update={update} tid="tec-sinistri" />
      <YN label="A conoscenza di azioni, omissioni o fatti da cui possano derivare richieste di risarcimento?" field="fatti_risc" formData={formData} update={update} tid="tec-fatti" />

      {showClaimsSheet && formData.sinistri_claims && formData.sinistri_claims.length > 0 && (
        <div className="mt-4 p-4 sm:p-5 bg-amber-50/50 border border-amber-200/60 rounded-2xl space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            In caso di risposta affermativa, compilare il <strong>Modulo CIS</strong> sottostante per ciascun sinistro o circostanza.
          </div>
          <ClaimsInfoSheet
            claims={formData.sinistri_claims}
            onClaimsChange={(updated) => update("sinistri_claims", updated)}
            config={CIS_TECNICA_CONFIG}
          />
        </div>
      )}

      <FileUploadZone label="Upload documenti aggiuntivi" hint="Carica eventuali documenti rilevanti a supporto (perizie, lettere, atti)"
        required={false}
        onFileUploaded={d => handleFileUpload("cis", d)} />
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Note aggiuntive</Label>
        <Textarea value={formData.det_sinistri || ""} onChange={e => update("det_sinistri", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="Eventuali ulteriori dettagli o circostanze rilevanti..." data-testid="tec-det-sin" />
      </div>
    </div>
  );

  const renderAssicPrec = () => (
    <div className="space-y-5">
      <YN label="Il Proponente &egrave; mai stato assicurato per la RC Professionale?" field="gia_assicurato" formData={formData} update={update} tid="tec-gia-ass" />
      {formData.gia_assicurato === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FI label="Assicuratori precedenti" required value={formData.assic_prec} onChange={v => update("assic_prec", v)} tid="tec-assic-prec" />
          <FI label="Data scadenza ultima copertura" type="date" value={formData.data_scad} onChange={v => update("data_scad", v)} tid="tec-data-scad" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FI label="Franchigia" value={formData.franch_prec} onChange={v => update("franch_prec", v)} tid="tec-franch-prec" />
            <FI label="Massimale" value={formData.mass_prec} onChange={v => update("mass_prec", v)} tid="tec-mass-prec" />
          </div>
          <FI label="Numero anni copertura continua" type="number" value={formData.anni_cop} onChange={v => update("anni_cop", v)} tid="tec-anni-cop" />
          <FI label="Ultimo Premio Pagato (&euro;)" required type="number" value={formData.ultimo_premio} onChange={v => update("ultimo_premio", v)} tid="tec-ultimo-premio" />
        </div>
      )}
    </div>
  );

  const renderDichiarazioni = () => (
    <div className="space-y-6">
      <p className="text-sm text-[#4A5E51]">Conferma di aver preso visione dei documenti.</p>
      <DichiarazioniBlock formData={formData} update={update} />
      <div className="flex items-start gap-3 p-4 bg-[#F5F4F0] rounded-xl">
        <Checkbox id="set_info_tec" checked={formData.set_informativo || false} onCheckedChange={v => update("set_informativo", v)} className="mt-0.5 data-[state=checked]:bg-[#163321]" data-testid="tec-set-info" />
        <label htmlFor="set_info_tec" className="text-xs text-[#4A5E51] leading-relaxed cursor-pointer">
          <strong>Set Informativo</strong> <span className="text-[#D97B58]">*</span><br />
          Dichiaro di aver ricevuto e preso visione dei documenti del SET INFORMATIVO (Regolamento IVASS n&deg; 41/2018).
        </label>
      </div>
    </div>
  );

  return (
    <><Header />
    <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="tec-form-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-12">
        <div className="text-center mb-8">
          <button onClick={() => { setSelectedCategory(null); setStep(0); setFormData({}); setFiles({}); }} className="text-xs text-[#D97B58] hover:underline mb-2 inline-block" data-testid="tec-back">&larr; Cambia professione</button>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2">RC Professioni Tecniche</h1>
          <p className="text-sm text-[#4A5E51]">{cat?.label}</p>
        </div>
        <StepProgress steps={steps} currentStep={step} onStepClick={setStep} progress={progress} testIdPrefix="tec" />
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 mb-6 shadow-sm" data-testid={`tec-step-${step}`}>
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">{steps[step]}</h2>
          {renderStep()}
        </div>
        <FormNavigation
          step={step} totalSteps={totalSteps}
          onPrev={() => step === 0 ? setSelectedCategory(null) : setStep(s => s - 1)}
          onNext={() => setStep(s => s + 1)}
          onSubmit={handleSubmit} submitting={submitting} canSubmit={!!formData.privacy}
          prevLabel={step === 0 ? "Professioni" : "Indietro"} testIdPrefix="tec"
        />
      </div>
      <PrivacyLinks className="mt-6 mb-4" />
    </main><Footer /></>
  );
}
